import React, { useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { useVsiAppContext } from "../stores/VsiApp";
import { VsiBubble, VsiH6, VsiSpanText } from "../styled/App.styled";
import XRangeChart from "../components/XRangeChart";
import { Row, Col, Spinner } from "reactstrap";
import { lightOrDark } from "../utils/Helpers";
import themeStyles from "../utils/themeStyles";

const grtSixMo = "#0066b5";
const lstSixMo = "orange";
const atSuppEnd = "red";

const legendH = 10;
const legendW = 25;
const border = "1px solid lightgray";

export default function EndOfSupportCalendar({ params }) {
  const { theme, client: { clientId } } = useVsiAppContext();

  const route = useMemo(() => `/api/v6/report/business_unit_support_lifecycle?client_id=${clientId}`, [clientId]);
  const [{ recordset: supportData }, { isLoading }] = useDataWithRoute(route);

  const { allGroups, allDataObj, min, max } = useMemo(() => {

    const groupsArr = [];
    const groupsObj = {};
    const today = Date.now();
    let min, max = null;
    let color = grtSixMo;

    supportData.forEach(datum => {
      const bu = datum[params?.[0]?.value];
      if (!groupsObj[bu]) {
        groupsObj[bu] = {};
      }
      if (!groupsArr.includes(bu)) {
        groupsArr.push(bu);
      }

      const x = new Date(datum.purchase_date).getTime() < 0 ? Date.now() : new Date(datum.purchase_date).getTime();
      const x2 = new Date(datum.end_of_maintenance_date).getTime();

      if (min === null || x < min) {
        min = x;
      }
      if (max === null || x2 > max) {
        max = x2;
      }

      if (x2 - today < 0) {
        color = atSuppEnd;
      } else if (x2 - today <= 15778800000) {
        color = lstSixMo;
      }

      groupsObj[bu][datum.device_name] = {
        x,
        x2,
        color
      };
    });

    return {
      allGroups: groupsArr,
      allDataObj: groupsObj,
      min,
      max
    };
  }, [supportData, params]);

  const minDate = new Date(min);
  const maxDate = new Date(max);

  const minDateYear = minDate.getFullYear();
  const maxDateYear = maxDate.getFullYear();

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <>
      <VsiBubble>
        <Row>
          <Col xs={12}>
            <div className={"d-flex flex-row justify-content-start"}>
              <div className={"d-flex flex-row justify-content-center align-items-center ps-1"}
                   style={{
                     borderBottom: "1px solid lightgray"
                   }}>
                <div style={{
                  height: legendH,
                  width: legendW,
                  background: grtSixMo,
                  border
                }} />
                <VsiSpanText className={"ps-1"}>{"> 6 months"}</VsiSpanText>
              </div>
              <div className={"d-flex flex-row justify-content-center align-items-center ps-1"}
                   style={{
                     borderBottom: "1px solid lightgray"
                   }}>
                <div style={{
                  height: legendH,
                  width: legendW,
                  background: lstSixMo,
                  border
                }} />
                <VsiSpanText className={"ps-1"}>{"< 6 months"}</VsiSpanText>
              </div>
              <div className={"d-flex flex-row justify-content-center align-items-center ps-1"}
                   style={{
                     borderBottom: "1px solid lightgray"
                   }}>
                <div style={{
                  height: legendH,
                  width: legendW,
                  background: atSuppEnd,
                  border
                }} />
                <VsiSpanText className={"ps-1"}>{"At Support End"}</VsiSpanText>
              </div>
            </div>
          </Col>
        </Row>
      </VsiBubble>
      <VsiBubble>
        <Row>
          {allGroups.map((center, centerIndex) => {
            const chartCategories = Object.keys(allDataObj[center]);
            const chartData = chartCategories.map((cat, catI) => {
              return {
                ...allDataObj[center][cat],
                y: catI
              };
            });

            const bgColor = centerIndex === 0 || centerIndex % 2 === 0 ? themeStyles[theme].altBgColor : themeStyles[theme].cardBgColor;

            return (
              <Col xs={12} key={`${center}_support_area_dc_${centerIndex}`}>
                <Row>
                  <Col xs={1}
                       className={"d-flex flex-column justify-content-center align-items-center"}>
                    <VsiH6 style={{
                      color: lightOrDark(bgColor) === "light" ? "black" : "white"
                    }}>{center}</VsiH6>
                  </Col>
                  <Col xs={11}>
                    <XRangeChart data={chartData}
                                 titleName={center}
                                 min={Date.UTC(minDateYear, 1, 1)}
                                 max={Date.UTC(maxDateYear, 12, 31)}
                                 categories={chartCategories}
                                 height={100 * chartData.length} />
                  </Col>
                </Row>
              </Col>
            );
          })}
        </Row>
      </VsiBubble>
    </>
  );
}
